import { ClipboardList, FileBarChart, Megaphone, PenLine, RefreshCw, ShieldCheck, Sparkles } from "lucide-react";

import { PLATFORM_META } from "../../Pages/ContentGenerator/workflow/platformMeta.jsx";

/** Content prompts (GenerationAIConfig), by generation step. Only the superadmin sees them; agencies edit just the adaptation prompts. */
export const CONTENT_PROMPTS = [
  {
    key: "campaign_generation_prompt",
    step: "campaign_generation",
    label: "Generación de campañas",
    description: "Ideas de posts para campañas.",
    Icon: Megaphone,
  },
  {
    key: "individual_content_prompt",
    step: "individual_content",
    label: "Contenido individual",
    description: "Contenido base (brief) de cada pieza.",
    Icon: PenLine,
  },
  {
    key: "iteration_prompt",
    step: "iteration",
    label: "Iteración de contenido",
    description: "Mejora de contenido existente.",
    Icon: RefreshCw,
  },
];

export const CONTENT_PROMPT_TITLES = Object.fromEntries(
  CONTENT_PROMPTS.map((prompt) => [prompt.key, prompt.label])
);

export const ADAPTATION_PROMPTS = [
  { key: "adaptation_prompt_blog", platform: "blog", label: "Blog" },
  { key: "adaptation_prompt_linkedin", platform: "linkedin", label: "LinkedIn" },
  { key: "adaptation_prompt_instagram", platform: "instagram", label: "Instagram" },
  { key: "adaptation_prompt_facebook", platform: "facebook", label: "Facebook" },
  { key: "adaptation_prompt_google_business_profile", platform: "google_business_profile", label: "Google Business" },
  { key: "adaptation_prompt_tiktok", platform: "tiktok", label: "TikTok" },
].map((prompt) => ({
  ...prompt,
  color: PLATFORM_META[prompt.platform].color,
  PlatformIcon: PLATFORM_META[prompt.platform].Icon,
  description: `Reglas de adaptación para ${prompt.label}.`,
}));

// Backend fallback for any platform without its own adaptation prompt.
export const GENERIC_ADAPTATION_PROMPT_KEY = "platform_adaptation_prompt";

export const PROMPT_STEPS = ["campaign_generation", "individual_content", "iteration", "adaptation"];

/** Report prompts (ReportAIConfig), one config per agency. */
export const REPORT_PROMPTS = [
  {
    key: "report_rules_prompt",
    label: "Reglas comunes",
    description: "Tono y límites que siguen todos los textos del informe.",
    Icon: ShieldCheck,
  },
  {
    key: "report_generation_prompt",
    label: "Resumen ejecutivo",
    description: "Resumen ejecutivo del informe.",
    Icon: FileBarChart,
  },
  {
    key: "section_analysis_prompt",
    label: "Lecturas IA por sección",
    description: "Lectura de tráfico, visibilidad y rendimiento web.",
    Icon: Sparkles,
  },
  {
    key: "period_details_prompt",
    label: "Detalle del periodo",
    description: "Texto detallado de resultados, trabajo, contenidos y acuerdos del periodo.",
    Icon: ClipboardList,
  },
];

export const EMPTY_PROMPT_PLACEHOLDER = "Vacío: se usa el prompt base de Markezy";

const hasText = (value) => typeof value === "string" && value.trim().length > 0;

/**
 * Prompts that apply to `steps` (and `platforms`, for adaptation), with the text
 * the backend will use. `source`: "agency" | "generic" (agency fallback) | "default".
 * `ownValue` is the prompt's own field, the one that gets edited.
 */
export const resolveAgencyPrompts = (config, steps, platforms, includeContent) => {
  const content = CONTENT_PROMPTS.filter((prompt) => includeContent && steps.includes(prompt.step)).map((prompt) => {
    const value = config?.[prompt.key] || "";
    return { ...prompt, settingsKey: prompt.key, value, ownValue: value, source: hasText(value) ? "agency" : "default" };
  });

  if (!steps.includes("adaptation")) return content;

  const generic = config?.[GENERIC_ADAPTATION_PROMPT_KEY] || "";
  const adaptation = ADAPTATION_PROMPTS.filter(
    (prompt) => !platforms?.length || platforms.includes(prompt.platform)
  ).map((prompt) => {
    const own = config?.[prompt.key] || "";
    const base = { ...prompt, settingsKey: prompt.key, ownValue: own };
    if (hasText(own)) return { ...base, value: own, source: "agency" };
    if (hasText(generic)) return { ...base, value: generic, source: "generic" };
    return { ...base, value: "", source: "default" };
  });

  return [...content, ...adaptation];
};
